import { z } from "zod";

// --- Design Tokens ---

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Must be a 6-digit hex color");

export const designTokenColorsSchema = z.object({
  primary: hexColor,
  secondary: hexColor,
  accent: hexColor,
  background: hexColor,
  surface: hexColor,
  text: hexColor,
  textMuted: hexColor,
  border: hexColor,
  error: hexColor,
  success: hexColor,
  warning: hexColor,
});

export const designTokenFontsSchema = z.object({
  display: z.string().min(1).optional(),
  body: z.string().min(1),
  mono: z.string().min(1).optional(),
});

export const designTokenSpacingSchema = z.object({
  scale: z.array(z.number().int().positive()).min(1),
});

export const designTokenRadiusSchema = z.object({
  sm: z.string().min(1),
  md: z.string().min(1),
  lg: z.string().min(1),
  full: z.string().min(1),
});

export const designTokenShadowsSchema = z.object({
  sm: z.string().min(1),
  md: z.string().min(1),
  lg: z.string().min(1),
});

export const designTokensSchema = z.object({
  colors: designTokenColorsSchema,
  fonts: designTokenFontsSchema,
  spacing: designTokenSpacingSchema,
  radius: designTokenRadiusSchema,
  shadows: designTokenShadowsSchema,
});

export type DesignTokens = z.infer<typeof designTokensSchema>;

// --- Design System Version Metadata ---
// Typed wrapper for source_versions.metadata.designTokens (JSONB column)
// All fields are optional — extraction may only identify a subset of tokens

export const designSystemExtractedTokensSchema = z.object({
  colors: designTokenColorsSchema.partial().optional(),
  fonts: designTokenFontsSchema.partial().optional(),
  spacing: designTokenSpacingSchema.partial().optional(),
  radius: designTokenRadiusSchema.partial().optional(),
  shadows: designTokenShadowsSchema.partial().optional(),
});

export type DesignSystemExtractedTokens = z.infer<
  typeof designSystemExtractedTokensSchema
>;

export interface DesignSystemVersionMetadata {
  designTokens?: DesignSystemExtractedTokens;
}

// --- Design Rules ---

export const designRuleCategorySchema = z.enum([
  "use",
  "avoid",
  "structure",
  "typography",
  "spacing",
  "interaction",
]);

export type DesignRuleCategory = z.infer<typeof designRuleCategorySchema>;

export const designRuleSchema = z.object({
  id: z.string().uuid(),
  rule: z.string().min(1),
  category: designRuleCategorySchema,
});

export const designRulesSchema = z.array(designRuleSchema).max(30);

export type DesignRule = z.infer<typeof designRuleSchema>;
export type DesignRules = z.infer<typeof designRulesSchema>;
